import { useEffect, useRef, useState } from "react";
import { Star, Loader2, Check, ImagePlus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

async function fileToAvatar(file) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const im = new Image();
    im.onload = () => res(im);
    im.onerror = rej;
    im.src = dataUrl;
  });
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext("2d");
  const scale = Math.max(size / img.width, size / img.height);
  const w = img.width * scale, h = img.height * scale;
  ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
  return canvas.toDataURL("image/jpeg", 0.82);
}

export function TestimonialInvite() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", business_name: "", quote: "", photo: null });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/testimonials/me").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (open) {
      const t = data?.testimonial;
      setForm({
        name: t?.name || user?.name || "",
        business_name: t?.business_name || "",
        quote: t?.quote || "",
        photo: t?.photo || null,
      });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data || !data.eligible) return null;

  const t = data.testimonial;
  const submit = async () => {
    if (form.quote.trim().length < 10) { toast.error("Please write at least a sentence."); return; }
    setSaving(true);
    try {
      await api.post("/testimonials", form);
      toast.success("Thank you! Your story is with our team for a quick review.");
      setOpen(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit. Please try again."); }
    finally { setSaving(false); }
  };

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const avatar = await fileToAvatar(f);
      setForm((s) => ({ ...s, photo: avatar }));
    } catch { toast.error("Could not read that image."); }
  };

  return (
    <>
      <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5" data-testid="testimonial-invite">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-amber-100"><Star size={20} className="text-amber-600" /></span>
            <div>
              <p className="font-heading text-base font-semibold text-amber-900">
                {t?.status === "approved" ? "Your story is live"
                  : t?.status === "pending" ? "Thanks. Your story is under review"
                  : data.first_sale ? "You made your first sale! Share your story" : "You launched! Share your story"}
              </p>
              <p className="mt-0.5 text-sm text-amber-800/80">
                {t?.status === "approved" ? "Thank you for helping other beginners get started."
                  : t?.status === "pending" ? "We'll publish it on the site once approved."
                  : "A sentence or two about your experience could help another beginner start."}
              </p>
            </div>
          </div>
          {t?.status !== "approved" && (
            <button data-testid="open-testimonial-btn" onClick={() => setOpen(true)}
              className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:bg-amber-700">
              {t ? "Edit my story" : "Share my story"}
            </button>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="testimonial-dialog">
          <DialogHeader><DialogTitle>Share your story</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              {form.photo ? (
                <div className="relative">
                  <img src={form.photo} alt="you" className="h-14 w-14 rounded-full object-cover" />
                  <button onClick={() => setForm((s) => ({ ...s, photo: null }))} className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-slate-800 text-white" data-testid="remove-photo-btn"><X size={12} /></button>
                </div>
              ) : (
                <button onClick={() => fileRef.current?.click()} data-testid="add-photo-btn"
                  className="grid h-14 w-14 place-items-center rounded-full border border-dashed border-brand-line text-slate-400 hover:text-brand"><ImagePlus size={20} /></button>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickPhoto} data-testid="photo-input" />
              <span className="text-xs text-slate-400">Optional photo</span>
            </div>
            <input data-testid="testimonial-name" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
              placeholder="Your full name" className="w-full rounded-lg border border-brand-line px-3 py-2 text-sm" />
            <input data-testid="testimonial-business" value={form.business_name} onChange={(e) => setForm((s) => ({ ...s, business_name: e.target.value }))}
              placeholder="Your business name" className="w-full rounded-lg border border-brand-line px-3 py-2 text-sm" />
            <textarea data-testid="testimonial-quote" value={form.quote} onChange={(e) => setForm((s) => ({ ...s, quote: e.target.value }))} rows={4}
              placeholder="What was it like going from an idea to your launch/first sale?" className="w-full rounded-lg border border-brand-line px-3 py-2 text-sm" />
            <button data-testid="submit-testimonial-btn" onClick={submit} disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Submit for review
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
